import React, { useEffect, useState } from 'react';
import './App.css';
import { IntlProvider } from 'react-intl';
import { BrowserRouter } from 'react-router-dom';
import Router from '@/Routes/Routes';
import { getLang } from '@/store/language/language';
import en from '@/translations/en.json';
import cn from '@/translations/cn.json';
import { useAppSelector } from '@/hooks/appHook';
import { TypeLang } from '@/store/language/language';
import { getUser } from '@/store/user/user.ts';
import { getFullVersion } from '@/utils/version.ts';

type Messages = Record<TypeLang, Record<string, string>>

interface ImportTranslates {
  default: Record<string, string>
}

const messages: Messages = {
  en,
  cn,
  tc: cn,
  kr: en,
  jp: en
}

const loadedMessages: Record<TypeLang, Record<string, string> | undefined> = {
  en,
  cn,
  tc: undefined,
  kr: undefined,
  jp: undefined
}

const sentryPromise = import('@sentry/react')

const App = () => {
  const lang = useAppSelector(getLang)
  const user = useAppSelector(getUser)
  const [translates, setTranslates] = useState<Record<string, string>>(messages[lang])

  useEffect(() => {
    if (loadedMessages[lang]) {
      setTranslates(loadedMessages[lang])
      return
    }
    let promise: Promise<ImportTranslates> | undefined = undefined
    let loading = true
    switch (lang) {
      case 'tc':
        promise = import('@/translations/tc.json')
        break
      case 'jp':
        promise = import('@/translations/jp.json')
        break
      case 'kr':
        promise = import('@/translations/kr.json')
        break
      default:
        return;
    }
    promise.then(({ default: newLang }) => {
      loadedMessages[lang] = newLang
      if (loading) {
        setTranslates(newLang)
      }
    })
    return () => {
      loading = false
    }
  }, [lang]);

  useEffect(() => {
    getFullVersion().then(ver => {
      sentryPromise.then(Sentry => {
        Sentry.setTag('app-version', ver)
      })
    })
  }, []);

  useEffect(() => {
    sentryPromise.then(Sentry => {
      Sentry.setUser({
        id: user?.memberId
      })
    })
  }, [user?.memberId]);

  return (
    <IntlProvider locale={lang} messages={translates}>
      <BrowserRouter>
        <Router/>
      </BrowserRouter>
    </IntlProvider>
  );
}

export default App;
